import { FeedbackRequestFeedbackTargetTypeEnum } from '@calm/evexia-web-sdk/generated/lib/models/feedback-request';
import createAiRouterApi from '@rhg/libs/api/create-ai-router';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    queryKeys,
    useMutation,
    useQuery,
    useQueryClient,
} from '@rhg/libs/react-query';
import { useActiveCircle } from '../use-active-circle';

interface UseAiFeedbackParams {
    responseId: string;
    contentId?: string;
    useful: boolean;
    feedbackTargetType: FeedbackRequestFeedbackTargetTypeEnum;
    rationale?: string;
}

export function useAiFeedback() {
    const { getAccessTokenSilently } = useAuth();
    const { activeCircle: circle } = useActiveCircle();
    const api = createAiRouterApi(getAccessTokenSilently);
    const queryClient = useQueryClient();

    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async ({
            responseId,
            contentId,
            useful,
            feedbackTargetType,
            rationale,
        }: UseAiFeedbackParams) => {
            if (!circle) {
                throw new Error(
                    'Cannot submit feedback: activeCircle is missing.'
                );
            }
            if (!circle.authorizationJWT) {
                throw new Error(
                    'Cannot submit feedback: circle.authorizationJWT is missing.'
                );
            }
            const response = await api.aiRouterResponsesCreateFeedbackV1({
                responseId,
                circleAuthorizationToken: circle.authorizationJWT,
                feedbackRequest: {
                    contentId,
                    useful,
                    feedbackTargetType,
                    rationale,
                },
            });
            return response;
        },
        onSuccess: async (_, variables) => {
            // Invalidate the feedback query for this responseId to trigger refetch
            await queryClient.invalidateQueries({
                queryKey: [queryKeys.aiFeedback, variables.responseId],
            });
        },
    });

    return {
        submitFeedback: mutate,
        submitFeedbackAsync: mutateAsync,
        ...otherProps,
    };
}

export function useAiFeedbackQuery(responseId?: string) {
    const { getAccessTokenSilently } = useAuth();
    const { activeCircle: circle } = useActiveCircle();
    const api = createAiRouterApi(getAccessTokenSilently);

    return useQuery({
        queryKey: [queryKeys.aiFeedback, responseId],
        queryFn: async () => {
            if (!responseId) {
                throw new Error(
                    'Cannot fetch feedback: responseId is missing.'
                );
            }
            if (!circle) {
                throw new Error(
                    'Cannot fetch feedback: activeCircle is missing.'
                );
            }
            if (!circle.authorizationJWT) {
                throw new Error(
                    'Cannot fetch feedback: circle.authorizationJWT is missing.'
                );
            }
            const response = await api.aiRouterResponsesGetFeedbackV1({
                responseId,
                circleAuthorizationToken: circle.authorizationJWT,
            });
            return response;
        },
        enabled: !!responseId && !!circle?.authorizationJWT,
    });
}
